/**
 * پنل پذیرنده — «درگاه پذیرندگی» کاربر
 * =====================================
 * کاربر اینجا درگاه خودش را می‌سازد، کلیدها را می‌بیند، فاکتورهای
 * پرداخت‌شده را دنبال می‌کند و ماژول فروشگاهش را می‌گیرد.
 *
 * دو تصمیمِ رابط کاربری که عمدی‌اند:
 *
 * ۱. **رمز فقط یک بار** — پس از ساخت یا تعویض، رمز در یک پنجره نشان داده
 *    می‌شود و دیگر هرگز. اگر در فهرست می‌ماند، هر نگاهِ ناخواسته به صفحه
 *    یعنی لو رفتنِ همه‌ی درگاه‌ها.
 * ۲. **دلیلِ عدم صلاحیت پیش از کلیک** — اگر کاربر هنوز احراز هویت نکرده،
 *    دکمه‌ی ساخت را نشان می‌دهیم ولی دلیل را بالای صفحه می‌گوییم؛ نه اینکه
 *    بگذاریم کلیک کند و خطای مبهم بگیرد.
 */
import { useEffect, useState } from 'react'
import {
  Store, Plus, KeyRound, Power, RefreshCw, ShieldCheck, Download,
  ExternalLink, AlertTriangle, Wallet,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { PanelShell } from '../components/Layout'
import { Card, Button, Modal, Field, Empty, Rows, CopyButton, Badge, SectionHead } from '../components/UI'
import { get, post, patch } from '../lib/api'
import { toast } from '../store'
import { clsx, faDate, num, irt, asArray } from '../lib/format'

type Status = {
  eligible: boolean
  reasons: string[]
  kycLevel: number
  minKyc: number
  feePct: number
  maxKeys: number
  enabled: boolean
  selfService: boolean
}

const STATUS_LABEL: Record<string, [string, 'up' | 'down' | 'info' | 'gold' | 'muted']> = {
  new: ['ارز انتخاب نشده', 'muted'],
  pending: ['منتظر پرداخت', 'gold'],
  paid: ['پرداخت شد', 'up'],
  underpaid: ['کسری پرداخت', 'down'],
  expired: ['منقضی', 'muted'],
  cancelled: ['لغو شد', 'muted'],
}

export default function Merchant() {
  const [status, setStatus] = useState<Status | null>(null)
  const [keys, setKeys] = useState<any[] | null>(null)
  const [charges, setCharges] = useState<any[] | null>(null)
  const [stats, setStats] = useState<any>(null)
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState({ label: '', coins: '' })
  const [busy, setBusy] = useState(false)
  const [secret, setSecret] = useState<{ key: string; secret: string } | null>(null)

  const loadKeys = () =>
    get('/merchant/keys').then((r: any) => setKeys(asArray(r))).catch(() => setKeys([]))
  const loadCharges = () =>
    get('/merchant/charges').then((r: any) => setCharges(asArray(r, 'data'))).catch(() => setCharges([]))

  const load = () => {
    get('/merchant/status').then((r: any) => setStatus(r)).catch(() => setStatus(null))
    get('/merchant/stats').then((r: any) => setStats(r)).catch(() => setStats(null))
    loadKeys()
    loadCharges()
  }
  useEffect(() => { load() }, [])

  const create = async () => {
    setBusy(true)
    try {
      const r: any = await post('/merchant/keys', form)
      setOpen(false)
      setForm({ label: '', coins: '' })
      setSecret({ key: r.key, secret: r.secret })
      loadKeys()
    } catch (e: any) { toast.err(e.message) } finally { setBusy(false) }
  }

  const toggle = async (id: number) => {
    try { await post(`/merchant/keys/${id}/toggle`); loadKeys() }
    catch (e: any) { toast.err(e.message) }
  }

  const rotate = async (id: number, key: string) => {
    try {
      const r: any = await post(`/merchant/keys/${id}/rotate`)
      setSecret({ key, secret: r.secret })
      loadKeys()
    } catch (e: any) { toast.err(e.message) }
  }

  const rename = async (id: number, label: string) => {
    try { await patch(`/merchant/keys/${id}`, { label }); loadKeys() }
    catch (e: any) { toast.err(e.message) }
  }

  const blocked = status && !status.eligible

  return (
    <PanelShell title="درگاه پذیرندگی"
      subtitle="از مشتریان خودتان رمزارز بگیرید — پول مستقیم به موجودی همین حساب می‌نشیند">

      {blocked && (
        <Card className="mb-5 border-gold/25 bg-gold/[.05]">
          <div className="flex gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
            <div className="text-sm leading-7 text-slate-300">
              {status!.reasons.map((r, i) => <div key={i}>{r}</div>)}
              {status!.kycLevel < status!.minKyc && (
                <Link to="/kyc" className="mt-2 inline-flex items-center gap-1 text-brand-300 hover:underline">
                  رفتن به احراز هویت <ExternalLink className="h-3.5 w-3.5" />
                </Link>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* ── خلاصه */}
      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[
          ['فاکتور پرداخت‌شده', stats ? num(stats.paid) : '—'],
          ['در انتظار پرداخت', stats ? num(stats.pending) : '—'],
          ['مجموع دریافتی', stats ? irt(stats.paidIrt) : '—'],
          ['کارمزد ارزینو', status ? `${num(status.feePct, 2)}٪` : '—'],
        ].map(([label, value]) => (
          <Card key={label as string} className="!p-4">
            <div className="text-[11px] text-slate-500">{label}</div>
            <div className="mt-1 num text-lg font-bold text-white">{value}</div>
          </Card>
        ))}
      </div>

      {/* ── کلیدها */}
      <SectionHead title="درگاه‌های شما"
        subtitle="هر درگاه یک کلید و یک رمز دارد؛ همان‌ها را در ماژول فروشگاه وارد می‌کنید"
        action={<Button onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> درگاه جدید</Button>} />

      <Card className="!p-0">
        <div className="p-2">
          {keys === null ? <div className="p-3"><Rows n={2} /></div>
            : keys.length === 0
              ? <Empty text="هنوز درگاهی نساخته‌اید" icon={<Store className="h-6 w-6" />} />
              : keys.map((k) => (
                <div key={k.id} className="flex flex-wrap items-center gap-3 border-b border-white/[.05] p-4 last:border-0">
                  <span className={clsx('grid h-10 w-10 place-items-center rounded-xl border',
                    k.active ? 'border-brand-500/25 bg-brand-500/10 text-brand-300'
                             : 'border-white/10 bg-white/[.03] text-slate-500')}>
                    <KeyRound className="h-4 w-4" />
                  </span>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-white">{k.label}</div>
                    <div className="num text-[11px] text-slate-500" dir="ltr">{k.keyMasked}</div>
                  </div>
                  <Badge kind={k.active ? 'up' : 'muted'}>{k.active ? 'فعال' : 'غیرفعال'}</Badge>
                  {k.coins && <Badge kind="info">{k.coins}</Badge>}
                  <span className="text-[11px] text-slate-500">{faDate(k.createdAt, false)}</span>
                  <div className="mr-auto flex flex-wrap items-center gap-2">
                    <CopyButton text={k.key} label="کپی کلید" />
                    <button onClick={() => rotate(k.id, k.key)} title="رمز تازه"
                      className="rounded-lg p-2 text-slate-500 transition hover:bg-white/[.06] hover:text-brand-300">
                      <RefreshCw className="h-4 w-4" />
                    </button>
                    <button onClick={() => toggle(k.id)} title={k.active ? 'غیرفعال کردن' : 'فعال کردن'}
                      className={clsx('rounded-lg p-2 transition hover:bg-white/[.06]',
                        k.active ? 'text-slate-500 hover:text-down' : 'text-slate-500 hover:text-up')}>
                      <Power className="h-4 w-4" />
                    </button>
                  </div>
                  <input
                    className="input !py-1.5 w-full text-xs lg:w-64"
                    defaultValue={k.label}
                    onBlur={(e) => { if (e.target.value !== k.label) rename(k.id, e.target.value) }}
                    placeholder="نام درگاه" />
                </div>
              ))}
        </div>
      </Card>

      {/* ── فاکتورها */}
      <SectionHead title="فاکتورها" subtitle="آخرین درخواست‌های پرداختی که فروشگاه شما ساخته" />
      <Card className="!p-0">
        <div className="hidden lg:block">
          <table className="w-full text-right text-xs">
            <thead className="text-slate-500">
              <tr className="border-b border-white/[.06]">
                {['شماره سفارش', 'مبلغ', 'ارز', 'دریافتی', 'کارمزد', 'خالص', 'وضعیت', 'تاریخ'].map((h) => (
                  <th key={h} className="px-4 py-3 font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(charges || []).map((c) => (
                <tr key={c.id} className="border-b border-white/[.04] last:border-0">
                  <td className="num px-4 py-3 text-white" dir="ltr">{c.orderId}</td>
                  <td className="num px-4 py-3">{irt(c.amountIrt)}</td>
                  <td className="px-4 py-3">{c.symbol || '—'}</td>
                  <td className="num px-4 py-3" dir="ltr">{c.received ? num(c.received, 8) : '—'}</td>
                  <td className="num px-4 py-3 text-slate-500" dir="ltr">{c.fee ? num(c.fee, 8) : '—'}</td>
                  <td className="num px-4 py-3 text-up" dir="ltr">{c.net ? num(c.net, 8) : '—'}</td>
                  <td className="px-4 py-3">
                    <Badge kind={(STATUS_LABEL[c.status] || ['—', 'muted'])[1]}>
                      {(STATUS_LABEL[c.status] || [c.status])[0]}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-slate-500">{faDate(c.createdAt, false)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* نسخه موبایل — جدول هشت‌ستونی روی گوشی خوانده نمی‌شود */}
        <div className="lg:hidden">
          {(charges || []).map((c) => (
            <div key={c.id} className="border-b border-white/[.05] p-4 last:border-0">
              <div className="mb-2 flex items-center justify-between gap-2">
                <span className="num text-sm font-semibold text-white" dir="ltr">{c.orderId}</span>
                <Badge kind={(STATUS_LABEL[c.status] || ['—', 'muted'])[1]}>
                  {(STATUS_LABEL[c.status] || [c.status])[0]}
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-y-1 text-[11px] text-slate-400">
                <span>مبلغ</span><span className="num text-left text-white">{irt(c.amountIrt)}</span>
                <span>ارز</span><span className="text-left">{c.symbol || '—'}</span>
                <span>خالص دریافتی</span>
                <span className="num text-left text-up" dir="ltr">{c.net ? num(c.net, 8) : '—'}</span>
                <span>تاریخ</span><span className="text-left">{faDate(c.createdAt, false)}</span>
              </div>
            </div>
          ))}
        </div>

        {charges !== null && charges.length === 0 && (
          <Empty text="هنوز فاکتوری ساخته نشده" icon={<Wallet className="h-6 w-6" />} />
        )}
        {charges === null && <div className="p-3"><Rows n={3} /></div>}
      </Card>

      {/* ── راهنما */}
      <Card className="mt-5">
        <h3 className="mb-3 flex items-center gap-2 font-bold text-white">
          <Download className="h-4 w-4 text-brand-300" /> ماژول فروشگاه خود را بگیرید
        </h3>
        <p className="text-xs leading-7 text-slate-400">
          برای ووکامرس، WHMCS، پرستاشاپ و اوپن‌کارت ماژول آماده داریم؛ برای بقیه‌ی
          سیستم‌ها هم کتابخانه‌ی PHP و مستندات کامل REST.
        </p>
        <Link to="/merchants" className="mt-3 inline-flex items-center gap-1.5 text-sm text-brand-300 hover:underline">
          آموزش نصب و دانلود ماژول‌ها <ExternalLink className="h-3.5 w-3.5" />
        </Link>
      </Card>

      {/* ── ساخت درگاه */}
      <Modal open={open} onClose={() => setOpen(false)} title="ساخت درگاه پذیرندگی">
        <Field label="نام درگاه" hint="فقط برای شناختن خودتان — مثلاً نام فروشگاه">
          <input className="input" placeholder="فروشگاه من" value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })} />
        </Field>
        <Field label="ارزهای مجاز (اختیاری)" hint="با کاما جدا کنید؛ خالی یعنی همه‌ی ارزهای پشتیبانی‌شده">
          <input className="input num" dir="ltr" placeholder="USDT,BTC,ETH" value={form.coins}
            onChange={(e) => setForm({ ...form, coins: e.target.value })} />
        </Field>
        {status && (
          <p className="mt-2 text-[11px] leading-6 text-slate-500">
            کارمزد ارزینو از هر پرداخت: <b className="num text-slate-300">{num(status.feePct, 2)}٪</b> —
            از مبلغ رسیده کسر می‌شود و باقی به موجودی شما می‌نشیند.
          </p>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setOpen(false)}>انصراف</Button>
          <Button onClick={create} loading={busy}>ساخت درگاه</Button>
        </div>
      </Modal>

      {/* ── نمایش یک‌باره‌ی رمز */}
      <Modal open={!!secret} onClose={() => setSecret(null)} title="رمز درگاه — فقط همین یک بار">
        <div className="mb-4 flex gap-3 rounded-xl border border-gold/25 bg-gold/[.06] p-3">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
          <p className="text-xs leading-6 text-slate-300">
            این رمز دوباره نمایش داده نمی‌شود. همین حالا در ماژول فروشگاهتان ذخیره‌اش کنید.
            اگر گم شد، از دکمه‌ی «رمز تازه» یکی دیگر بگیرید — رمز قبلی همان لحظه باطل می‌شود.
          </p>
        </div>
        <Field label="API Key">
          <div className="flex items-center gap-2">
            <input className="input num flex-1" dir="ltr" readOnly value={secret?.key || ''} />
            <CopyButton text={secret?.key || ''} />
          </div>
        </Field>
        <Field label="Secret (کلید امضا)">
          <div className="flex items-center gap-2">
            <input className="input num flex-1" dir="ltr" readOnly value={secret?.secret || ''} />
            <CopyButton text={secret?.secret || ''} />
          </div>
        </Field>
        <div className="mt-4 flex justify-end">
          <Button onClick={() => setSecret(null)}>ذخیره کردم</Button>
        </div>
      </Modal>
    </PanelShell>
  )
}
